// Header.js - Habitia Design System
import React, { useState, useEffect, useRef } from "react";
import styles from "./Header.module.css";
import { useUserProfile } from "../hooks/useUserProfile";
import logo from "../assets/logo.png";
import { useBuilding } from "../context/BuildingContext";
import { useTranslation } from "react-i18next";
import { 
  FaBars, 
  FaSearch, 
  FaBuilding, 
  FaBell, 
  FaCalendarAlt, 
  FaChevronDown,
  FaFileAlt,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaTools,
  FaNewspaper,
  FaSwimmingPool,
  FaFolderOpen,
  FaHardHat,
  FaCheckCircle,
  FaPeopleCarry,
  FaHome,
  FaRobot,
  FaDoorOpen
} from "react-icons/fa";

import RoleSwitcher from "./RoleSwitcher";

const Header = ({ setSelected, sidebarCollapsed, setSidebarCollapsed, userType, onLogout = () => {} }) => {
  const { photoURL, initial } = useUserProfile();
  const [showMenu, setShowMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const { buildings, selectedBuilding, setSelectedBuilding, userUnitsInBuilding, selectedUnit, setSelectedUnit, selectedBuildingHasAmenities, soloAlquilerEdificio } = useBuilding();
  const { t } = useTranslation("header");
  const roleLabel = (r) => (r ? t(`roles.${r}`, r) : "—");
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);

  // BUG-78 fix: Ctrl+K keyboard shortcut to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // BUG-79 fix: Close search when clicking outside (ref-based, no race condition)
  const handleSearchBlur = (e) => {
    if (searchContainerRef.current && searchContainerRef.current.contains(e.relatedTarget)) {
      return; // Focus moved to another element inside the container
    }
    setShowSearchResults(false);
  };

  // Determinar la pantalla principal según el tipo de usuario
  const getHomeScreen = () => {
    if (userType === 2) return "homesuperadmin";  // SuperAdmin
    if (userType === 1) return "homeadmin";       // Admin
    return "inicio";                               // Residente
  };

  // Opciones de búsqueda rápida con íconos de Habitia
  const rawSearchOptions = [
    { id: "inicio", label: "Inicio", description: "Panel principal", icon: FaHome },
    { id: "documentos", label: "Documentos", description: "Facturas y recibos", icon: FaFileAlt },
    { id: "reuniones", label: "Asambleas", description: "Convocatorias y actas", icon: FaClipboardList },
    soloAlquilerEdificio
      ? { id: "expensas", label: "Alquiler", description: "Pago del alquiler", icon: FaFileInvoiceDollar }
      : { id: "expensas", label: "Gastos Comunes", description: "Pagos y gastos", icon: FaFileInvoiceDollar },
    { id: "problema", label: "Incidencias", description: "Reportes y seguimiento", icon: FaTools },
    { id: "novedades", label: "Novedades", description: "Últimas noticias", icon: FaNewspaper },
    { id: "amenities", label: "Amenities", description: "Reservas", icon: FaSwimmingPool },
    { id: "documentacion", label: "Documentación", description: "Reglamentos", icon: FaFolderOpen },
    { id: "obras", label: "Obras y Proyectos", description: "Trabajos en curso", icon: FaHardHat },
    { id: "revisiones", label: "Revisiones", description: "Inspecciones", icon: FaCheckCircle },
    { id: "prestadores", label: "Proveedores", description: "Servicios externos", icon: FaPeopleCarry },
    { id: "habi", label: "Habi", description: "Asistente inteligente", icon: FaRobot },
  ];
  // Asambleas: solo propietarios vigentes (igual que el Sidebar y el servidor).
  const isCurrentOwner = (userUnitsInBuilding || []).some(
    (u) => u.userRole === "Propietario" && !u.isHistorical
  );
  // Filtra Amenities si el edificio no tiene amenities configurados.
  const searchOptions = rawSearchOptions.filter(
    (opt) =>
      !(opt.id === "amenities" && selectedBuildingHasAmenities === false) &&
      !(opt.id === "reuniones" && !isCurrentOwner)
  );

  const filteredOptions = searchQuery 
    ? searchOptions.filter(opt => 
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        opt.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchOptions;

  const handleSearchSelect = (optionId) => {
    setSelected(optionId);
    setSearchQuery("");
    setShowSearchResults(false);
  };

  return (
    <header className={styles.header}>
      {/* Left Section: Menu Toggle + Logo */}
      <div className={styles.left}>
        <button 
          className={styles.menuToggle}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={sidebarCollapsed ? t("showMenu") : t("hideMenu")}
        >
          <FaBars />
        </button>
        
        <div className={styles.brand} onClick={() => setSelected(getHomeScreen())} title={t("goHome")}>
          <img src={logo} alt="Habitia" className={styles.logo} />
        </div>
      </div>

      {/* Center Section: Search Bar */}
      <div className={styles.center}>
        <div className={styles.searchContainer} ref={searchContainerRef}>
          <FaSearch className={styles.searchIcon} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t("search")}
            className={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowSearchResults(true)}
            onBlur={handleSearchBlur}
          />
          <span className={styles.searchShortcut}>Ctrl + K</span>
          
          {showSearchResults && (
            <div className={styles.searchResults}>
              <div className={styles.searchResultsHeader}>
                {searchQuery ? "Resultados" : "Accesos rápidos"}
              </div>
              {filteredOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <div
                    key={option.id}
                    className={styles.searchResultItem}
                    tabIndex={-1}
                    onMouseDown={() => handleSearchSelect(option.id)}
                  >
                    <div className={styles.searchResultIcon}>
                      <IconComponent />
                    </div>
                    <div className={styles.searchResultText}>
                      <span className={styles.searchResultLabel}>{option.label}</span>
                      <span className={styles.searchResultDescription}>{option.description}</span>
                    </div>
                  </div>
                );
              })}
              {filteredOptions.length === 0 && (
                <div className={styles.searchNoResults}>
                  No se encontraron resultados para "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right Section: Building Selector + Actions + User */}
      <div className={styles.right}>
        {/* Selector de edificio */}
        <div className={styles.selector}>
          <div className={styles.selectorIconWrapper}>
            <FaBuilding />
          </div>
          <select
            className={styles.selectorSelect}
            value={selectedBuilding?.id || ""}
            onChange={(e) => {
              const building = buildings.find(
                (b) => b.id.toString() === e.target.value
              );
              setSelectedBuilding(building);
              setSelected("inicio");
            }}
          >
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <FaChevronDown className={styles.selectorChevron} />
        </div>

        {/* Unidad y rol del residente (siempre visible cuando tiene al menos una propiedad) */}
        {userType === 0 && (selectedUnit || userUnitsInBuilding?.[0]) && (
          <div className={styles.selector}>
            <div className={styles.selectorIconWrapper} style={{ background: "#059669" }}>
              <FaDoorOpen />
            </div>
            {userUnitsInBuilding?.length > 1 ? (
              <>
                <select
                  className={styles.selectorSelect}
                  value={selectedUnit?.id || ""}
                  onChange={(e) => {
                    const unit = userUnitsInBuilding.find((u) => u.id === e.target.value);
                    setSelectedUnit(unit || null);
                    setSelected("inicio");
                  }}
                >
                  {userUnitsInBuilding.map((u) => (
                    <option key={u.id} value={u.id}>
                  {t("unitRole", { number: u.number ?? "—", role: roleLabel(u.userRole) })}
                </option>
                  ))}
                </select>
                <FaChevronDown className={styles.selectorChevron} />
              </>
            ) : (
              <span className={styles.unitLabel}>
                {(() => {
                  const u = selectedUnit || userUnitsInBuilding?.[0];
                  return t("unitRoleDot", { number: u?.number ?? "—", role: roleLabel(u?.userRole) });
                })()}
              </span>
            )}
          </div>
        )}

        {/* Notificaciones */}
        <button className={styles.iconButton} title={t("notifications")}>
          <FaBell />
        </button>

        {/* Calendario/Amenities — solo si el edificio tiene amenities configurados */}
        {selectedBuildingHasAmenities !== false && (
          <button
            className={styles.iconButton}
            title={t("reserveAmenities")}
            onClick={() => setSelected("amenities")}
          >
            <FaCalendarAlt />
          </button>
        )}

        {/* Usuario */}
        <div className={styles.userMenu} onClick={() => setShowMenu(!showMenu)}>
          {photoURL ? (
            <img src={photoURL} alt="Usuario" className={styles.avatar} />
          ) : (
            <div className={styles.avatarLetter}>{initial}</div>
          )}
          <FaChevronDown className={styles.chevron} />
          {showMenu && (
            <div className={styles.dropdown}>
              <div className={styles.dropdownItem} onClick={() => { setSelected("perfil"); setShowMenu(false); }}>{t("menu.profile")}</div>
              <div className={styles.dropdownItem} onClick={() => { setSelected("configuracion"); setShowMenu(false); }}>{t("menu.settings")}</div>
              <RoleSwitcher variant="menu" />
              <div className={styles.dropdownDivider}></div>
              <div className={styles.dropdownItem} onClick={() => { onLogout?.(); setShowMenu(false); }}>{t("menu.logout")}</div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
